// src/modules/app-views/omnichannel/pages/Init/components/ChatMenuHeader.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Input, Segmented, Tooltip } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { PiChatSlashBold, PiChatDotsBold } from 'react-icons/pi';
import { GrGroup } from 'react-icons/gr';
import { VscArchive } from 'react-icons/vsc';
import translateX from 'utils/translateX';

// *******************************************************************************
// COMPONENT
// *******************************************************************************

/**
 * ChatMenuHeader — toolbar with:
 * - Retractable search input (expands left from button position)
 * - Filter toggle buttons (search, groups, archived with badges)
 * - Active/Closed toggle buttons with conversation count badges
 */
const ChatMenuHeader = ({
  searchValue,
  onSearchChange,
  showGroups,
  onToggleGroups,
  groupCount,
  showArchived,
  onToggleArchived,
  archivedCount,
  viewMode,
  onViewModeChange,
  activeCount,
  closedCount,
  token,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef(null);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isSearchOpen]);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenSearch = () => setIsSearchOpen(true);

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    // Clear search value on close
    if (searchValue) onSearchChange({ target: { value: '' } });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="chat-menu-toolbar"
      style={{
        borderBottomColor: token.colorBorderSecondary,
        minHeight: 68,
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Animation keyframes + Segmented vertical alignment */}
      <style>{`
        @keyframes omni-search-expand {
          from { max-width: 0; opacity: 0; }
          to   { max-width: 600px; opacity: 1; }
        }
        .chat-view-mode-segment {
          height: 40px;
          border-radius: 4px !important;
          width: 70px !important;
        }
        .chat-view-mode-segment .ant-segmented-group,
        .chat-view-mode-segment .ant-segmented-item,
        .chat-view-mode-segment .ant-segmented-item-label {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .chat-view-mode-segment .ant-segmented-item {
          border-radius: 4px !important;
          height: 38px;
          // margin-left: 1px;
          min-width: 34px !important;
          max-width: 34px !important;
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          gap: 6,
          paddingLeft: 11,
          paddingRight: 11,
        }}
      >
        {/* Retractable search input — fills all space to the LEFT of the buttons */}
        {isSearchOpen && (
          <Input
            ref={inputRef}
            placeholder={`${translateX('search')}...`}
            value={searchValue}
            onChange={onSearchChange}
            onKeyDown={e => e.key === 'Escape' && handleCloseSearch()}
            size="small"
            style={{
              flex: 1,
              minWidth: 0,
              animation: 'omni-search-expand 0.25s ease forwards',
              overflow: 'hidden',
              height: 40,
            }}
          />
        )}

        {/* Buttons — always fixed to the right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        >
          {/* Search / Close toggle button — only changes icon, position stays the same */}
          <Tooltip title={isSearchOpen ? translateX('close_search') : translateX('search')}>
            <Button
              size="small"
              type={isSearchOpen ? 'primary' : 'default'}
              icon={
                isSearchOpen ? (
                  <CloseOutlined style={{ fontSize: 14 }} />
                ) : (
                  <SearchOutlined style={{ fontSize: 14 }} />
                )
              }
              onClick={isSearchOpen ? handleCloseSearch : handleOpenSearch}
            />
          </Tooltip>

          {/* Groups filter */}
          {/* Badge: override the default translate(50%,-50%) so the RIGHT edge is fixed.
              As the count grows (2+ digits) the badge expands leftward, never overflowing right. */}
          <Tooltip title={showGroups ? translateX('hide_groups') : translateX('show_groups')}>
            <Badge count={groupCount} size="small" overflowCount={99}
              style={{ transform: 'translateY(-50%)', right: -4 }}>
              <Button
                size="small"
                type={showGroups ? 'primary' : 'default'}
                icon={<GrGroup style={{ fontSize: 14, display: 'flex' }} />}
                onClick={onToggleGroups}
              />
            </Badge>
          </Tooltip>

          {/* Archived filter */}
          <Tooltip title={showArchived ? translateX('hide_archived') : translateX('show_archived')}>
            <Badge count={archivedCount} size="small" overflowCount={99}
              style={{ transform: 'translateY(-50%)', right: -4 }}>
              <Button
                size="small"
                type={showArchived ? 'primary' : 'default'}
                icon={<VscArchive style={{ fontSize: 14, display: 'flex' }} />}
                onClick={onToggleArchived}
              />
            </Badge>
          </Tooltip>

          {/* Active / Closed segmented toggle */}
          <Segmented
            className="chat-view-mode-segment"
            size="small"
            value={viewMode}
            onChange={onViewModeChange}
            options={[
              {
                value: 'active',
                label: (
                  <Tooltip title={translateX('active_conversations')} placement="bottom">
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <PiChatDotsBold style={{ fontSize: 14 }} />
                    </span>
                  </Tooltip>
                ),
              },
              {
                value: 'closed',
                label: (
                  <Tooltip title={translateX('closed_conversations')} placement="bottom">
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <PiChatSlashBold style={{ fontSize: 14 }} />
                    </span>
                  </Tooltip>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChatMenuHeader;
